import { Tree } from '@/utils/tree';
import logger from '@/utils/logger';
import { merge } from 'lodash';
import { Status } from '@/utils/useData';
import { FilterMode, TreeMode } from './config';
import { buildSearchQuery } from './iql-builder';

function setTreeMode(state, mode) {
  if (!Object.values(TreeMode).includes(mode)) {
    logger.warn(`Invalid tree mode: ${mode}.`);
    return;
  }
  state.treeMode = mode;
}

function tree(state) {
  const { tree: _t, treeMode } = state;
  return _t[treeMode];
}

function selectTreeNode(state, nodeId) {
  const t = tree(state);
  t.selectedNode = nodeId;
}

function setTreeNodes(state, treeNodes) {
  const t = tree(state);
  t.nodes = treeNodes;
}

function setExpandedNodes(state, expandedNodes) {
  const t = tree(state);
  t.expandedNodes = expandedNodes;
}

function setTreeStatus(state, status) {
  const t = tree(state);
  t.status = status;
}

function toggleTreeNode(state, nodeId) {
  const t = tree(state);
  const treeNode = Tree.find(t.nodes, (node) => node.id === nodeId);
  if (!treeNode) {
    logger.warn(`Cannot toggle tree node! Missing node: ${nodeId}.`);
    return;
  }

  // TODO: Review this state mutation:
  treeNode.expanded = !treeNode.expanded;

  // FIXME: use store.subscribe to persist state
  // __saveTreeState(state);
}

function setTreeRecursiveMode(state, recursiveMode) {
  state.treeRecursiveMode = recursiveMode;
}

function enableFilterParam({ filterParams }, { name, showOptions }) {
  const filterParam = filterParams[name];
  if (!filterParam) return;

  filterParam.active = true;
  filterParam.value = [];
  filterParam.showOptions = Boolean(showOptions);
}

function disableFilterParam({ filterParams }, { name }) {
  const filterParam = filterParams[name];
  if (!filterParam) return;

  filterParam.active = false;
  filterParam.value = [];
}

function disableFilterParams({ filterParams }, { filterNames } = {}) {
  const byNamePredicate = filterNames ? (f) => filterNames.includes(f.name) : () => true;
  Object.values(filterParams)
    .filter(byNamePredicate)
    .forEach((f) => disableFilterParam({ filterParams }, { name: f.name }));
}

/**
 * Toggles the dropdown of the specified filter parameter
 * @param {*} state
 * @param {*} payload
 */
function setFilterParamOptions({ filterParams }, { name, showOptions }) {
  const filterParam = filterParams[name];
  if (!filterParam) return;

  filterParam.showOptions = Boolean(showOptions);
}

// eslint-disable-next-line
function setFilterParam({ filterParams }, { name, value }) {
  const filterParam = filterParams[name];
  if (!filterParam) return;

  filterParam.value = value;
}

function setFilterParams(state, filterParams) {
  state.filterParams = filterParams;
}

function setFilterMode(state, { mode = FilterMode.BASIC, query = '', isQueryDirty = false } = {}) {
  if (!Object.values(FilterMode).includes(mode)) {
    throw new Error('invalid IQL mode');
  }

  state.filterMode = mode;
  if (mode === FilterMode.ADVANCED) {
    state.advancedModeFilterQuery = query;
  }

  // reset the `basic` query, if the query was modified in `advanced` mode
  if (mode === FilterMode.BASIC && isQueryDirty) {
    disableFilterParams(state);
  }
}

function setAdvancedModeQuery(state, query) {
  state.advancedModeFilterQuery = query;
}

function toggleFiltersToolbar(state) {
  if (state.isFiltersToolbarOpen) {
    state.isFiltersToolbarOpen = false;
    disableFilterParams(state);
    state.advancedModeFilterQuery = '';

    state.simpleSearchCompactMode = false;
    state.simpleSearchTerm = '';
  } else {
    state.isFiltersToolbarOpen = true;
    state.simpleSearchCompactMode = true;
  }
}

/**
 * Switches to Advanced Mode and resets the IQL query
 * (useful when overwriting the query based on route params)
 */
function resetFilterQuery(state, query) {
  state.isFiltersToolbarOpen = true;
  state.filterMode = FilterMode.ADVANCED;
  state.advancedModeFilterQuery = query;
  selectTreeNode(state, '');
}

function setSavedFilters(state, savedFilters) {
  state.savedFilters = savedFilters;
}

function loadSavedFilter(state, { name }) {
  const filter = state.savedFilters.find((f) => f.name === name);
  if (!filter) return;

  const {
    iql_chips: savedFilterParams,
    iqlmode: savedFilterMode,
    iql: savedFilterQuery,
  } = filter;

  if (!savedFilterParams) {
    logger.warn(`Missing filter parameters for saved filter: ${name}. It seems like the iql was not parsed correctly! Fallback to advancedMode!`);
    state.isFiltersToolbarOpen = true;
    setFilterMode(state, { mode: FilterMode.ADVANCED, query: savedFilterQuery });
    return;
  }

  // TODO: move shape remapping to the back-end or to the action
  const savedFilterParamsMap = savedFilterParams.reduce((result, fp) => {
    // eslint-disable-next-line
    result[fp.name] = {
      active: true,
      name: fp.name,
      value: Array.isArray(fp.value) ? fp.value : [fp.value],
    };
    return result;
  }, {});

  disableFilterParams(state);
  state.filterParams = merge(state.filterParams, savedFilterParamsMap);

  // TODO: double-check that the saved IQL is the same as
  // the one constucted from the parameters

  state.isFiltersToolbarOpen = true;
  setFilterMode(state, { mode: savedFilterMode, query: savedFilterQuery });
}

function setSimpleSearchTerm(state, term) {
  state.simpleSearchTerm = term;
}

function runSimpleSearchQuery(state, term) {
  state.simpleSearchTerm = term;
  state.simpleSearchCompactMode = true;
  state.isFiltersToolbarOpen = true;

  disableFilterParams(state);
  setFilterParam(state, {
    name: 'fts_filter',
    value: [term],
  });
  state.advancedModeFilterQuery = buildSearchQuery(null, [term]);

  selectTreeNode(state, '');
}

function setSimpleSearchCompactMode(state, compactMode) {
  state.simpleSearchCompactMode = compactMode;
  state.simpleSearchTerm = '';
}

function setColumns(state, columns) {
  state.columns = columns;
}

function toggleColumn({ columns }, { name }) {
  const column = columns[name];
  if (!column) {
    logger.warn(`Unknow column: ${name}`);
    return;
  }
  column.active = !column.active;
}

const docId = (doc) => `[${doc.node_id}][${doc.url}]`;

// TODO: refactor/review these mutations
function setProp(obj, key, value) {
  obj[key] = value; // eslint-disable-line
}

function deleteProp(obj, key) {
  delete obj[key]; // eslint-disable-line
}

function toggleDocuments({ selectedDocumentRows }, docs) {
  const isDocSelected = (doc) => Boolean(selectedDocumentRows[docId(doc)]);
  // TODO: refactor/review these mutations
  const toggle = docs.every(isDocSelected) ? deleteProp : setProp;
  docs.forEach((doc) => {
    if (doc.copypages) {
      doc.copypages.forEach((copy) => toggle(selectedDocumentRows, docId(copy), copy));
    }

    toggle(selectedDocumentRows, docId(doc), doc);
  });
}

function shiftSelectDocuments({ selectedDocumentRows }, { docs, selected }) {
  const toggle = selected ? setProp : deleteProp;
  docs.forEach((doc) => {
    if (doc.copypages) {
      doc.copypages.forEach((copy) => toggle(selectedDocumentRows, docId(copy), copy));
    }

    toggle(selectedDocumentRows, docId(doc), doc);
  });
}

function inspectDocument(state, doc) {
  if (!doc) return;
  state.inspectedDocument = doc;
}

function setDocuments(state, { page = 1, documents = [] }) {
  state.documentsPerPage = {
    [page]: documents,
    ...state.documentsPerPage,
  };
}

function setDocumentCopypages(state, { nodeId, copypages = [] }) {
  const document = Object.values(state.documentsPerPage).flat().find((doc) => doc.node_id === nodeId);
  document.copypages = copypages;
}

function setTotalDocumentsCount(state, count) {
  if (Number.isNaN(count)) {
    state.totalDocumentsCount = 0;
    throw new Error('invalid documents count', count);
  }
  state.totalDocumentsCount = parseInt(count, 10);
}

function setDocumentsStatus(state, { status, message }) {
  if (!Object.values(Status).includes(status)) {
    throw new Error('invalid documents status', status);
  }
  state.documentsStatus = status;
  state.documentsStatusMessage = message;
}

function clearDocuments(state) {
  state.documentsPerPage = {};
  state.selectedDocumentRows = {};
  state.inspectedDocument = null;
}

function setInsertMode(state, insertMode) {
  state.isInsertMode = insertMode;
}

function setEntityTableDirty(state) {
  state.isEntityTableDirty = true;
}

function cleanEntityTable(state) {
  state.isEntityTableDirty = false;
}

function setShiftSelectAnchor(state, anchor) {
  state.shiftSelectAnchor = anchor;
}

function sortTableMainSetting(state, sortBy) {
  state.tablePrimarySort = sortBy;
}

function sortTableUserPreference(state, sortBy) {
  state.tableUserSort = sortBy;
}

export default {
  setTreeMode,
  setAdvancedModeQuery,
  setFilterMode,
  resetFilterQuery,
  toggleDocuments,
  shiftSelectDocuments,
  setSimpleSearchTerm,
  runSimpleSearchQuery,
  setSimpleSearchCompactMode,
  toggleFiltersToolbar,
  setFilterParam,
  enableFilterParam,
  disableFilterParam,
  disableFilterParams,
  setFilterParamOptions,
  setFilterParams,
  setColumns,
  toggleColumn,
  selectTreeNode,
  toggleTreeNode,
  setTreeNodes,
  setExpandedNodes,
  setTreeRecursiveMode,
  setTreeStatus,
  setSavedFilters,
  loadSavedFilter,
  inspectDocument,
  setDocuments,
  setDocumentCopypages,
  setTotalDocumentsCount,
  setDocumentsStatus,
  clearDocuments,
  setInsertMode,
  setEntityTableDirty,
  cleanEntityTable,
  setShiftSelectAnchor,
  sortTableMainSetting,
  sortTableUserPreference,
};
